/**
 * THE LEVEL CURVE — 1 to 99, the RuneScape XP curve.
 *
 * The table below is HARDCODED rather than computed at load, on purpose.
 * The generating formula is
 *
 *     xp(L) = floor( (1/4) * sum_{n=1}^{L-1} floor( n + 300 * 2^(n/7) ) )
 *
 * which calls Math.pow. ECMAScript does not require Math.pow to be
 * bit-identical across engines, so a table computed at load is a table that can
 * differ by one XP between two browsers — and one XP either side of a floor
 * boundary is a level. A save that levels on one engine and not another is a
 * save bug that no patch can undo, so the numbers are frozen here as data and
 * curve.test.ts re-derives them from the formula to prove the freeze is honest.
 *
 * Values MEASURED against the published curve: level 2 = 83,
 * level 92 = 6,517,253, level 99 = 13,034,431.
 */

export const MAX_LEVEL = 99;

/** XP_TABLE[L - 1] is the total XP required to be level L. Index 0 is level 1 = 0 XP. */
export const XP_TABLE: readonly number[] = [
  0, 83, 174, 276, 388, 512, 650, 801, 969, 1154, 1358, 1584, 1833, 2107, 2411, 2746, 3115, 3523,
  3973, 4470, 5018, 5624, 6291, 7028, 7842, 8740, 9730, 10824, 12031, 13363, 14833, 16456, 18247,
  20224, 22406, 24815, 27473, 30408, 33648, 37224, 41171, 45529, 50339, 55649, 61512, 67983, 75127,
  83014, 91721, 101333, 111945, 123660, 136594, 150872, 166636, 184040, 203254, 224466, 247886,
  273742, 302288, 333804, 368599, 407015, 449428, 496254, 547953, 605032, 668051, 737627, 814445,
  899257, 992895, 1096278, 1210421, 1336443, 1475581, 1629200, 1798808, 1986068, 2192818, 2421087,
  2673114, 2951373, 3258594, 3597792, 3972294, 4385776, 4842295, 5346332, 5902831, 6517253, 7195629,
  7944614, 8771558, 9684577, 10692629, 11805606, 13034431,
];

/** Total XP needed to reach `level`. Clamped to [1, 99]; never throws. */
export function xpForLevel(level: number): number {
  if (!Number.isFinite(level)) return 0;
  const l = Math.trunc(level);
  if (l <= 1) return 0;
  const idx = (l >= MAX_LEVEL ? MAX_LEVEL : l) - 1;
  return XP_TABLE[idx] ?? 0;
}

/**
 * Level for a total XP amount. Binary search, so it is O(log 99) rather than a
 * scan — a settle over the hold calls this on every tick.
 * Clamped to [1, 99]; never throws. Every hostile input — NaN, either infinity,
 * a negative — returns level 1, because corruption is not an achievement.
 */
export function levelForXp(xp: number): number {
  if (!Number.isFinite(xp) || xp <= 0) return 1;
  if (xp >= XP_TABLE[MAX_LEVEL - 1]!) return MAX_LEVEL;
  let lo = 1;
  let hi = MAX_LEVEL;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (xp >= XP_TABLE[mid - 1]!) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * XP still owed before the next level. Zero at 99, where there is no next level.
 * Hostile input is sanitised to 0 first, so this can never return NaN — a NaN
 * leaking into a progress bar is a save-shaped bug wearing a UI costume.
 */
export function xpToNextLevel(xp: number): number {
  const safe = Number.isFinite(xp) ? Math.max(0, Math.trunc(xp)) : 0;
  const level = levelForXp(safe);
  if (level >= MAX_LEVEL) return 0;
  return xpForLevel(level + 1) - safe;
}

/**
 * The formula the table was frozen from. Used only by curve.test.ts, which
 * asserts it reproduces XP_TABLE on the engine the tests run on.
 */
export function computeXpTable(): number[] {
  const table: number[] = [0];
  let points = 0;
  for (let l = 1; l < MAX_LEVEL; l++) {
    points += Math.floor(l + 300 * Math.pow(2, l / 7));
    table.push(Math.floor(points / 4));
  }
  return table;
}
